import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { uploadReceiptImage } from '../../api/receiptUploadApi';
import { callExtractReceipt } from '../../api/extractReceiptApi';
import { hasReceiptScanAvvConsent } from '../../config/receiptScanAccess';
import logger from '../../utils/logger';

/** Options for {@link useScanCapture}. */
export interface UseScanCaptureOptions {
  /** Called after a successful upload + extraction — parent refreshes the Unified Inbox list. */
  onCaptured: () => void;
}

/** Return value of {@link useScanCapture}. */
export interface UseScanCapture {
  /** Uploads a receipt image and triggers KI extraction (Foto/Scan path). */
  processImage: (file: File) => Promise<void>;
  /** `true` while an image is being uploaded/extracted. */
  uploading: boolean;
  /** Last error message (already localised), or `null`. */
  error: string | null;
  /** Last success message (already localised), or `null`. */
  success: string | null;
}

/**
 * Extracts the Foto/Scan capture logic of the (now removed) `ScanCaptureCard`
 * into a reusable hook: `uploadReceiptImage` → `callExtractReceipt` →
 * `scanned_receipts/{id}`, with the same MIME/size/auth error mapping. The DOM
 * (input, buttons) stays with the consumer (`CaptureBar`); this hook owns only
 * the async processing + uploading/error/success state.
 *
 * @param opts - `onCaptured` callback fired after a successful extraction.
 * @returns processing function + uploading/error/success state.
 */
export function useScanCapture(opts: UseScanCaptureOptions): UseScanCapture {
  const { onCaptured } = opts;
  const { t } = useTranslation('posteingang');
  const { currentUser, sellerData } = useAuth();

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const processImage = useCallback(
    async (file: File): Promise<void> => {
      if (!currentUser) {
        setError(t('unifiedInbox.scan.errorAuth', 'Bitte melden Sie sich erneut an.'));
        return;
      }

      // M13a-GA harter Client-Guard (Defense-in-Depth): kein Upload/Scan ohne
      // gültige AVV-Zustimmung — verhindert auch den Storage-Upload vor Consent.
      if (!hasReceiptScanAvvConsent(sellerData?.receiptScanAvvAcceptedVersion)) {
        setError(
          t(
            'unifiedInbox.scan.avvRequired',
            'Bitte stimmen Sie zuerst der Auftragsverarbeitungsvereinbarung zum KI-Belegscanner zu.',
          ),
        );
        return;
      }

      setUploading(true);
      setError(null);
      setSuccess(null);

      try {
        const result = await uploadReceiptImage(file);
        logger.info('useScanCapture: upload complete', result.storageRef);
        await callExtractReceipt({
          receiptId: result.receiptId,
          storageRef: result.storageRef,
          mimeType: result.mimeType,
          originalFilename: result.originalFilename,
        });
        setSuccess(
          t('unifiedInbox.scan.uploadSuccess', 'Beleg gespeichert — erscheint gleich in der Liste unten.'),
        );
        onCaptured();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        logger.error(err, 'useScanCapture: upload error');
        if (message.includes('avv_consent_required')) {
          // Server-seitige AVV-Ablehnung (Defense-in-Depth-Sichtbarkeit).
          setError(
            t(
              'unifiedInbox.scan.avvRequired',
              'Bitte stimmen Sie zuerst der Auftragsverarbeitungsvereinbarung zum KI-Belegscanner zu.',
            ),
          );
        } else if (message.includes('MIME type') || message.includes('Unsupported')) {
          setError(t('unifiedInbox.scan.errorMime', 'Dieses Dateiformat wird nicht unterstützt.'));
        } else if (message.includes('too large') || message.includes('10 MB')) {
          setError(t('unifiedInbox.scan.errorSize', 'Die Datei ist zu groß (max. 10 MB).'));
        } else {
          setError(t('unifiedInbox.scan.errorUpload', 'Der Upload ist fehlgeschlagen.'));
        }
      } finally {
        setUploading(false);
      }
    },
    [currentUser, sellerData?.receiptScanAvvAcceptedVersion, t, onCaptured],
  );

  return { processImage, uploading, error, success };
}

export default useScanCapture;
